/**
 * Vorbelegungen für den großen Composer (NewMessageDialog.open) aus dem
 * Chat-Verlauf heraus: neue E-Mail an den Kontakt, ausführliche Antwort,
 * Weiterleiten, erneut versenden.
 *
 * Kundenfeedback 25.10.2026 Teil 3, I: Antworten und Weiterleiten belegen
 * nur vor (Empfänger, „AW:“/„WG:“-Betreff, zitierter Verlauf, beim
 * Weiterleiten die Original-Anhänge) – geschrieben wird überall mit
 * derselben Werkzeugleiste.
 */
import type { ChatConversation, ChatMessage, Counterpart, Viewer } from './chatModel'

export type ComposeOptions = Record<string, unknown>

const REPLY_PREFIX = /^\s*(aw|re):/i
const FORWARD_PREFIX = /^\s*(wg|fw|fwd):/i

function escapeHtml (text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function formatDateTime (value: string | null | undefined): string {
  if (!value) {
    return ''
  }
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('de-DE')
}

/** Absender einer Nachricht für Zitat-/Weiterleitungs-Kopf („Name <adresse>“). */
export function messageAuthor (m: ChatMessage): string {
  const name = (m.senderName || m.userName || '').trim()
  const email = (m.senderEmail || '').trim()
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${escapeHtml(name)} &lt;${escapeHtml(email)}&gt;`
  }
  return escapeHtml(name || email)
}

/** Betreff mit AW:, außer er trägt schon ein Antwort-Kürzel. */
export function replySubject (subject: string | null | undefined): string {
  const base = (subject || '').trim()
  if (!base) {
    return ''
  }
  return REPLY_PREFIX.test(base) ? base : `AW: ${base}`
}

/** Betreff mit WG:, außer er trägt schon ein Weiterleitungs-Kürzel. */
export function forwardSubject (subject: string | null | undefined): string {
  const base = (subject || '').trim()
  if (!base) {
    return ''
  }
  return FORWARD_PREFIX.test(base) ? base : `WG: ${base}`
}

/**
 * Zitat der beantworteten Nachricht („Am … schrieb …:“ + Inhalt), wie es
 * auch beim Antworten auf eine Posteingangs-E-Mail vorbelegt wird.
 */
export function replyQuoteHtml (m: ChatMessage | null | undefined): string {
  if (!m?.content) {
    return ''
  }
  const who = messageAuthor(m)
  const when = formatDateTime(m.createdAt)
  let intro = ''
  if (who && when) {
    intro = `Am ${when} schrieb ${who}:`
  } else if (who) {
    intro = `${who} schrieb:`
  }
  return `${intro ? `<p>${intro}</p>` : ''}<blockquote>${m.content}</blockquote>`
}

/** Kopf und Inhalt einer weitergeleiteten Nachricht. */
export function forwardQuoteHtml (m: ChatMessage, conv: ChatConversation | null): string {
  const lines = [
    ['Von', messageAuthor(m)],
    ['Gesendet', escapeHtml(formatDateTime(m.createdAt))],
    ['Betreff', escapeHtml((conv?.subject || '').trim())],
  ].filter(([, value]) => value).map(([label, value]) => `<b>${label}:</b> ${value}`)
  const head = lines.length > 0 ? `<p>${lines.join('<br>')}</p>` : ''
  return `<p>---------- Weitergeleitete Nachricht ----------</p>${head}${m.content || ''}`
}

/** Empfänger für den Kontakt: Adresse der Gegenseite bzw. Kollege bei internen Unterhaltungen. */
export function recipientPrefill (cp: Counterpart, viewer: Viewer): ComposeOptions {
  if (cp.kind === 'team') {
    const colleague = cp.team.find(t => t.idUser !== viewer.idUser)
    return colleague ? { recipientId: colleague.idUser } : {}
  }
  const email = cp.emails[0] || cp.email
  return email ? { prefillExternal: { email, name: cp.name } } : {}
}

function caseOf (conv: ChatConversation | null | undefined): ComposeOptions {
  return conv?.idCase ? { caseId: conv.idCase, caseNumber: conv.caseNumber || '' } : {}
}

/** Antwort mit allen Optionen (Empfängerliste, Cc/Bcc, Signaturwahl) im Composer. */
export function replyPrefill (cp: Counterpart, conv: ChatConversation | null, viewer: Viewer): ComposeOptions {
  const subject = replySubject(conv?.subject)
  const quote = replyQuoteHtml(conv?.lastMessage)
  return {
    ...recipientPrefill(cp, viewer),
    ...caseOf(conv),
    ...(subject ? { prefillSubject: subject } : {}),
    ...(quote ? { prefillHtml: quote } : {}),
  }
}

/**
 * Weiterleiten: WG:-Betreff, Kopf (Von/Gesendet/Betreff) und Inhalt. Die
 * Original-Anhänge lädt useComposeActions und reicht sie als
 * `attachmentFiles` nach.
 */
export function forwardPrefill (m: ChatMessage, conv: ChatConversation | null): ComposeOptions {
  return {
    ...caseOf(conv),
    prefillSubject: forwardSubject(conv?.subject),
    prefillHtml: forwardQuoteHtml(m, conv),
  }
}

/** "Als Anhang weiterleiten": die Nachricht als PDF im Anhang. */
export function pdfForwardPrefill (conv: ChatConversation | null, file: File): ComposeOptions {
  return { ...caseOf(conv), prefillSubject: forwardSubject(conv?.subject), attachmentFiles: [file] }
}

export function resendPrefill (m: ChatMessage | null, conv: ChatConversation | null): ComposeOptions {
  return {
    ...caseOf(conv),
    ...(conv?.subject ? { prefillSubject: conv.subject } : {}),
    ...(m?.content ? { prefillHtml: m.content } : {}),
  }
}
